import { Facebook, Instagram, Linkedin, BookOpen, Users2, Heart } from "lucide-react";
import { TikTok } from "@/components/icons/TikTok";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { OrgShape } from "@/components/Decorations";
import { RichContent } from "@/components/RichContent";
import { usePageContent } from "@/lib/usePageContent";
import lottaImage from "@/assets/lottaellonen-vaalit-2.1.jpg";

const FALLBACK = {
  hero_nickname: "Mörrikkä",
  hero_intro:
    "<p><strong>PÄÄTTÄVÄISYYTTÄ SINISELLÄ SYDÄMELLÄ.</strong></p><p>Mie uskon koulutukseen, kulttuuriin ja yrittäjyyteen yhteiskunnan perustana. Suomen tulee olla paikka, jossa jokaisella on yhtäläinen mahdollisuus menestyä ja elää hyvää elämää erilaisuuksistaan riippumatta. Ajattelen, että oikeistolaista talouspolitiikkaa voi toteuttaa myös inhimillisesti sinisellä sydämellä. Yhteisöllisempi Suomi rakentuu sillä, että on valmis rakentamaan ystävyyttä populismin sijaan ja tekemään arjen hyviä tekoja toisille.</p><p>Tutut tuntee miut myös lempinimellä <span data-color=\"primary\">Mörrikkä</span>.</p><p>Olen ehdolla eduskuntavaaleissa 2027 Oulun vaalipiirissä.</p>",
  hero_chip_1: "Nuorten puolella",
  hero_chip_2: "Sivistys",
  hero_chip_3: "Yritteliäisyys",
  hero_cta: "Tutustu Lottaan →",
  vision_title: "Visio",
  vision_card_1_title: "KOULUTUS",
  vision_card_1_desc: "Laadukas koulutus on tulevaisuuden perusta",
  vision_card_2_title: "NUORET",
  vision_card_2_desc: "Nuorten ääni kuuluu päätöksissä",
  vision_card_3_title: "YHTEISÖLLISYYS",
  vision_card_3_desc: "Luottamus ja yhdessä tekeminen",
  contact_title: "Tutustu ja ota yhteyttä",
  contact_card_1_title: "OSAAMINEN",
  contact_card_1_desc: "Koulutus, työ ja luottamustoimet",
  contact_card_2_title: "AJANKOHTAISTA",
  contact_card_2_desc: "Kirjoituksia ja kuulumisia",
  contact_card_3_title: "OTA YHTEYTTÄ",
  contact_card_3_desc: "Yhteydenottolomake",
};

const Index = () => {
  const { t } = usePageContent("index", FALLBACK);

  return (
    <Layout>
      {/* ── HERO ── */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, hsl(37 88% 96%) 0%, hsl(39 82% 92%) 30%, hsl(42 86% 72%) 100%)",
        }}
      >
        <OrgShape
          className="-top-32 right-[-8%] h-[520px] w-[560px]"
          color="radial-gradient(circle at 35% 40%, hsla(42,94%,84%,0.98) 0%, hsla(41,90%,66%,0.82) 62%, hsla(41,88%,58%,0) 100%)"
        />
        <OrgShape
          className="-left-28 top-[44%] h-[280px] w-[360px]"
          color="radial-gradient(circle at 55% 45%, hsla(38,92%,97%,0.92) 0%, hsla(39,75%,88%,0.55) 58%, hsla(39,70%,82%,0) 100%)"
          style={{ borderRadius: "48% 52% 58% 42% / 55% 45% 42% 58%" }}
        />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-14 md:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-20">
          <div className="relative z-10 order-2 max-w-xl md:order-1">
            <h1 className="mb-3 text-[3.5rem] font-bold leading-[0.88] tracking-tight text-secondary md:text-7xl lg:text-[5.5rem]">
              <span className="hero-title-lotta">Lotta</span><br />
              <span className="hero-title-ellonen">Ellonen</span>
            </h1>

            <div className="mb-5 h-[3px] w-48 rounded-full bg-primary" />

            <p className="mb-5 text-lg text-foreground">
              Kokoomus •{" "}
              <span className="font-semibold italic text-primary">{t("hero_nickname")}</span>
            </p>

            {(() => {
              const intro = t("hero_intro") || "";
              const isHtml = /<\/?[a-z][\s\S]*>/i.test(intro);
              let first = "";
              let body = "";
              if (isHtml) {
                const m = intro.match(/^([\s\S]*?<\/p>)([\s\S]*)$/i);
                if (m) {
                  first = m[1];
                  body = m[2].trim();
                } else {
                  first = intro;
                }
              } else {
                const [f, ...rest] = intro.split("\n");
                first = f;
                body = rest.join("\n").trim();
              }
              return (
                <div className="mb-8 mt-10">
                  <RichContent
                    html={first}
                    className="text-base font-bold uppercase leading-snug tracking-tighter text-secondary md:text-lg"
                    fallbackBoldClassName="font-bold text-secondary"
                  />
                  {body && (
                    <RichContent
                      html={body}
                      className="text-base leading-relaxed text-foreground/85 md:text-lg"
                      fallbackBoldClassName="font-bold text-secondary"
                    />
                  )}
                </div>
              );
            })()}

            <div className="mb-8 flex flex-wrap gap-2.5">
              {[t("hero_chip_1"), t("hero_chip_2"), t("hero_chip_3")].filter(Boolean).map((v) => (
                <span
                  key={v}
                  className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground"
                >
                  {v}
                </span>
              ))}
            </div>

            <div className="mb-8 flex flex-wrap gap-3">
              <Link
                to="/lotta"
                className="inline-flex items-center gap-2 rounded-full bg-secondary px-7 py-3.5 font-semibold text-secondary-foreground no-underline transition-transform hover:-translate-y-0.5"
              >
                {t("hero_cta")}
              </Link>
            </div>

            <div className="flex items-center gap-3">
              {[
                { href: "https://www.instagram.com/ellosenlotta", icon: Instagram },
                { href: "https://www.facebook.com/lottaellonen", icon: Facebook },
                { href: "https://www.linkedin.com/in/lottaellonen", icon: Linkedin },
                { href: "https://www.tiktok.com/@morrikka_", icon: TikTok },
              ].map(({ href, icon: Icon }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-card/85 p-3 text-secondary shadow-sm transition-transform hover:-translate-y-0.5"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="relative order-1 flex justify-center self-center md:order-2 md:justify-end">
            <svg
              className="absolute z-0 md:scale-125 lg:scale-150"
              style={{ width: "140%", height: "130%", top: "-15%", left: "-20%" }}
              viewBox="0 0 500 500"
              aria-hidden="true"
            >
              <path
                d="M250,450 C250,450 50,300 50,180 C50,100 110,40 180,40 C220,40 250,70 250,70 C250,70 280,40 320,40 C390,40 450,100 450,180 C450,300 250,450 250,450Z"
                fill="hsl(40,88%,68%)"
              />
            </svg>
            <div
              className="relative z-10 w-64 overflow-hidden rounded-[1.8rem] bg-card/70 p-1.5 shadow-2xl md:w-72 lg:w-80"
              style={{ aspectRatio: "800 / 1065" }}
            >
              <img
                src={lottaImage}
                alt="Lotta Ellonen"
                title="Kuvaaja: Koho, Eveliina 2025"
                className="h-full w-full rounded-[1.5rem] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── VISIO & MIULLE TÄRKEITÄ ── */}
      <section
        className="relative overflow-hidden px-6 py-16 lg:px-10"
        style={{
          background: "linear-gradient(180deg, hsl(39 88% 79%) 0%, hsl(40 86% 73%) 100%)",
        }}
      >
        <OrgShape
          className="-right-20 bottom-[-3rem] h-[280px] w-[320px]"
          color="radial-gradient(circle at 45% 45%, hsla(42,96%,84%,0.7) 0%, hsla(41,90%,64%,0.35) 62%, hsla(41,88%,56%,0) 100%)"
        />
        <svg className="pointer-events-none absolute left-[4%] bottom-[28%] hidden h-16 w-16 rotate-[-15deg] lg:block" viewBox="0 0 100 100" fill="none" aria-hidden="true">
          <path d="M50,90 C50,90 10,60 10,35 C10,20 22,8 36,8 C44,8 50,14 50,14 C50,14 56,8 64,8 C78,8 90,20 90,35 C90,60 50,90 50,90Z" fill="hsla(0,0%,100%,0.4)" />
        </svg>
        <svg className="pointer-events-none absolute right-[6%] top-[8%] hidden h-16 w-16 rotate-[10deg] lg:block" viewBox="0 0 100 100" fill="none" aria-hidden="true">
          <path d="M50,90 C50,90 10,60 10,35 C10,20 22,8 36,8 C44,8 50,14 50,14 C50,14 56,8 64,8 C78,8 90,20 90,35 C90,60 50,90 50,90Z" fill="hsla(0,0%,100%,0.4)" />
        </svg>

        <div className="relative mx-auto max-w-5xl">
          <div className="relative lg:pl-8">
            <div
              className="absolute left-[9px] hidden w-[3px] rounded-full bg-primary/50 lg:block"
              style={{ top: "10px", bottom: "10px" }}
            />

            {/* ── VISIO ── */}
            <div className="mb-16">
              <div className="relative mb-10 flex items-center gap-3 lg:-ml-8">
                <span className="relative z-10 h-5 w-5 shrink-0 rounded-full border-[3px] border-primary bg-card" />
                <h2 className="font-display text-3xl font-bold italic text-foreground md:text-4xl">
                  {t("vision_title")}
                </h2>
              </div>

              <div className="grid gap-5 md:grid-cols-3">
                {[
                  { icon: BookOpen, title: t("vision_card_1_title"), desc: t("vision_card_1_desc"), bg: "hsl(38 45% 93%)" },
                  { icon: Users2, title: t("vision_card_2_title"), desc: t("vision_card_2_desc"), bg: "hsl(206 42% 90%)" },
                  { icon: Heart, title: t("vision_card_3_title"), desc: t("vision_card_3_desc"), bg: "hsl(340 28% 91%)" },
                ].map(({ icon: Icon, title, desc, bg }) => (
                  <Link
                    key={title}
                    to="/politiikka"
                    className="rounded-2xl p-7 text-center shadow-sm transition-transform hover:-translate-y-1"
                    style={{ background: bg }}
                  >
                    <Icon className="mx-auto mb-4 h-9 w-9 text-primary" />
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-[0.14em] text-foreground">{title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* ── MIULLE TÄRKEITÄ ── */}
            <div>
              <div className="relative mb-10 flex items-center gap-3 lg:-ml-8">
                <span className="relative z-10 h-5 w-5 shrink-0 rounded-full border-[3px] border-primary bg-card" />
                <h2 className="font-display text-3xl font-bold italic text-foreground md:text-4xl">
                  {t("contact_title")}
                </h2>
              </div>

              <div className="grid gap-5 md:grid-cols-3">
                {[
                  { icon: BookOpen, title: t("contact_card_1_title"), desc: t("contact_card_1_desc"), bg: "hsl(38 45% 93%)", path: "/osaaminen" },
                  { icon: Users2, title: t("contact_card_2_title"), desc: t("contact_card_2_desc"), bg: "hsl(206 42% 90%)", path: "/blogi" },
                  { icon: Heart, title: t("contact_card_3_title"), desc: t("contact_card_3_desc"), bg: "hsl(340 28% 91%)", path: "/ota-yhteytta" },
                ].map(({ icon: Icon, title, desc, bg, path }) => (
                  <Link key={title} to={path} className="rounded-2xl p-7 text-center shadow-sm transition-transform hover:-translate-y-1" style={{ background: bg }}>
                    <Icon className="mx-auto mb-4 h-9 w-9 text-primary" />
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-[0.14em] text-foreground">{title}</h3>
                    <RichContent
                      html={desc}
                      className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground"
                      fallbackBoldClassName="font-semibold text-foreground"
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
